import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ogo_member_token";

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

function readToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

function writeToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* localStorage may be blocked */
  }
}

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => readToken());
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authed | guest

  const clearSession = useCallback(() => {
    writeToken(null);
    setToken(null);
    setUser(null);
    setStatus("guest");
  }, []);

  const authHeader = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const refreshMe = useCallback(async () => {
    const t = readToken();
    if (!t) {
      setUser(null);
      setStatus("guest");
      return null;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(data);
      setToken(t);
      setStatus("authed");
      return data;
    } catch (err) {
      if (err?.response?.status === 401) clearSession();
      else setStatus("guest");
      return null;
    }
  }, [clearSession]);

  // On mount: if a session_id fragment is present, defer /me — the AuthCallback
  // will exchange it and refresh state. Otherwise verify any stored token.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setStatus("loading");
      return;
    }
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async ({ email, password }) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      writeToken(data.token);
      setToken(data.token);
      setUser(data.user);
      setStatus("authed");
      return { ok: true, user: data.user };
    } catch (err) {
      return { ok: false, error: formatApiError(err?.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async ({ name, email, password }) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
      writeToken(data.token);
      setToken(data.token);
      setUser(data.user);
      setStatus("authed");
      return { ok: true, user: data.user };
    } catch (err) {
      return { ok: false, error: formatApiError(err?.response?.data?.detail) };
    }
  }, []);

  const exchangeOAuthSession = useCallback(async (sessionId) => {
    try {
      const { data } = await axios.post(`${API}/auth/oauth/session`, { session_id: sessionId });
      writeToken(data.token);
      setToken(data.token);
      setUser(data.user);
      setStatus("authed");
      return { ok: true, user: data.user };
    } catch (err) {
      setStatus("guest");
      return { ok: false, error: formatApiError(err?.response?.data?.detail) };
    }
  }, []);

  const startGoogleLogin = useCallback((returnTo = "/community") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + returnTo;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    const t = readToken();
    try {
      if (t) {
        await axios.post(`${API}/auth/logout`, null, {
          headers: { Authorization: `Bearer ${t}` },
        });
      }
    } catch {
      /* ignore — client clears session either way */
    }
    clearSession();
  }, [clearSession]);

  const value = {
    status,
    user,
    token,
    isAuthed: status === "authed" && !!user,
    isLoading: status === "loading",
    authHeader,
    refreshMe,
    login,
    register,
    logout,
    exchangeOAuthSession,
    startGoogleLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
